<script setup lang="ts">
import { useBuildChecker } from '@/core/useBuildChecker'
import AppSidebar from '@/layout/AppSidebar.vue'
import AppTopbar from '@/layout/AppTopbar.vue'
import { useLayout } from '@/layout/composables/layout'
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'

const { layoutConfig, layoutState, isSidebarActive } = useLayout()

useBuildChecker()

const outsideClickListener = ref<EventListener | null>(null)

watch(isSidebarActive, newVal => {
  if (newVal) {
    bindOutsideClickListener()
  } else {
    unbindOutsideClickListener()
  }
})

const containerClass = computed(() => {
  return {
    'layout-overlay': layoutConfig.menuMode === 'overlay',
    'layout-static': layoutConfig.menuMode === 'static',
    'layout-static-inactive': layoutState.staticMenuDesktopInactive && layoutConfig.menuMode === 'static',
    'layout-overlay-active': layoutState.overlayMenuActive,
    'layout-mobile-active': layoutState.staticMenuMobileActive
  }
})

const refreshPage = () => {
  window.location.reload()
}

function bindOutsideClickListener() {
  if (!outsideClickListener.value) {
    outsideClickListener.value = event => {
      if (isOutsideClicked(event)) {
        layoutState.overlayMenuActive = false
        layoutState.staticMenuMobileActive = false
        layoutState.menuHoverActive = false
      }
    }
    document.addEventListener('click', outsideClickListener.value)
  }
}

function unbindOutsideClickListener() {
  if (outsideClickListener.value) {
    document.removeEventListener('click', outsideClickListener.value)
    outsideClickListener.value = null
  }
}

function isOutsideClicked(event: Event) {
  const sidebarEl = document.querySelector('.layout-sidebar')
  const topbarEl = document.querySelector('.layout-menu-button')

  return !(sidebarEl?.isSameNode(event.target as Node) || sidebarEl?.contains(event.target as Node) || topbarEl?.isSameNode(event.target as Node) || topbarEl?.contains(event.target as Node))
}

onMounted(() => {
  const mediaQuery = window.matchMedia('(max-width: 991px)')

  const handler = (e: MediaQueryList | MediaQueryListEvent) => {
    if (e.matches) {
      layoutState.mobileMenu = true
    } else {
      layoutState.mobileMenu = false
    }
  }

  mediaQuery.addEventListener('change', handler)

  handler(mediaQuery)

  onUnmounted(() => {
    mediaQuery.removeEventListener('change', handler)
  })
})
</script>

<template>
  <div class="layout-wrapper" :class="containerClass">
    <app-topbar></app-topbar>
    <app-sidebar></app-sidebar>
    <div class="layout-main-container">
      <div class="layout-main">
        <router-view />
      </div>
    </div>
    <div class="layout-mask animate-fadein"></div>
  </div>
  <Toast />
  <Toast position="top-right" group="newBuild">
    <template #message="slotProps">
      <div class="flex flex-col items-start flex-auto p-3">
        <div class="flex items-center gap-3 mb-3">
          <i class="pi pi-exclamation-triangle text-black dark:text-white text-xl"></i>
          <span class="font-bold text-black dark:text-white">{{ slotProps.message.summary }}</span>
        </div>
        <div class="text-sm text-black dark:text-white mb-4">
          {{ slotProps.message.detail }}
        </div>
        <div class="flex gap-2 ml-auto">
          <Button size="small" :label="$t('build.updateAvailable.refresh')" severity="warn" @click="refreshPage" data-cy="build-refresh-button" />
        </div>
      </div>
    </template>
  </Toast>
</template>

<style lang="css" scoped>
:deep(.layout-topbar) {
  padding: 0.5rem 0.5rem 0 0.5rem !important;
}

:deep(.layout-sidebar) {
  top: 4.5rem !important;
  left: 0.5rem;
}

:deep(.layout-main-container) {
  padding: 4.5rem 0.5rem 0 0.5rem;
}

:deep(.layout-main) {
  padding-bottom: 0.5rem !important;
}
</style>
